import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar, Target, ShoppingBag, Mail, Users, Lock, Heart, ArrowRight, ChevronDown, User, LogOut, LogIn, MessageCircle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { CookieBanner } from "@/components/CookieBanner";
export default function FundraisingPage() {
  const {
    toast
  } = useToast();
  const {
    ptaId
  } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [ptaData, setPtaData] = useState<any>(null);
  const [sponsorshipData, setSponsorshipData] = useState<any>(null);
  const [user, setUser] = useState<any>(null);
  const [donateOpen, setDonateOpen] = useState(false);
  const [selectedAmount, setSelectedAmount] = useState("");
  const [members, setMembers] = useState<any[]>([]);
  const [events, setEvents] = useState<any[]>([]);
  const [goals, setGoals] = useState<any[]>([]);
  const [marketplaceItems, setMarketplaceItems] = useState<any[]>([]);
  const [unreadMessagesCount, setUnreadMessagesCount] = useState(0);
  const [selectedMarketplaceItem, setSelectedMarketplaceItem] = useState<any>(null);
  useEffect(() => {
    fetchPTAData();
    checkUser();
  }, [ptaId]);

  useEffect(() => {
    if (user && ptaId) {
      fetchUnreadMessagesCount();
    }
  }, [user, ptaId]);
  
  useEffect(() => {
    // If no ptaId in URL but user is logged in, try to get from parent record
    if (!ptaId && user) {
      getParentPTA();
    }
  }, [user, ptaId]);
  
  useEffect(() => {
    if (ptaData?.id) {
      fetchEvents();
      fetchGoals();
      fetchMarketplaceItems();
    }
  }, [ptaData?.id]);
  
  const getParentPTA = async () => {
    const { data } = await supabase
      .from('parents')
      .select('pta_id, ptas!inner(id, name, school_name)')
      .eq('user_id', user?.id)
      .single();
    
    if (data?.pta_id) {
      // Redirect to correct URL with PTA ID
      navigate(`/fundraising/${data.pta_id}`);
    }
  };
  const checkUser = async () => {
    const {
      data: {
        user
      }
    } = await supabase.auth.getUser();
    setUser(user);
  };

  const fetchUnreadMessagesCount = async () => {
    if (!user || !ptaId) return;

    try {
      // Get parent ID first
      const { data: parentData } = await supabase
        .from('parents')
        .select('id')
        .eq('user_id', user.id)
        .eq('pta_id', ptaId)
        .single();

      if (!parentData) return;

      // Note: shop_messages table structure needs to be checked
      // For now, set to 0 to avoid errors
      setUnreadMessagesCount(0);
    } catch (error) {
      console.error('Error fetching unread messages:', error);
    }
  };
  
  const fetchEvents = async () => {
    if (!ptaData?.id) return;
    
    const { data, error } = await supabase
      .from('events')
      .select('*')
      .eq('pta_id', ptaData.id)
      .eq('status', 'active')
      .order('event_date', { ascending: true })
      .limit(6);
    
    if (data) {
      setEvents(data);
    }
  };
  
  const fetchMarketplaceItems = async () => {
    if (!ptaData?.id) return;
    
    const { data, error } = await supabase
      .from('shop_items')
      .select('id, name, price, images, category')
      .eq('pta_id', ptaData.id)
      .eq('status', 'active')
      .eq('approval_status', 'approved')
      .or(`expires_at.is.null,expires_at.gt.${new Date().toISOString()}`)
      .order('created_at', { ascending: false })
      .limit(3);
    
    if (data) {
      setMarketplaceItems(data);
    }
  };
  
  const fetchGoals = async () => {
    if (!ptaData?.id) return;
    
    const { data, error } = await supabase
      .from('goals')
      .select('*')
      .eq('pta_id', ptaData.id)
      .eq('status', 'active')
      .eq('show_on_fundraising', true)
      .order('created_at', { ascending: false })
      .limit(6);
    
    if (data) {
      setGoals(data);
    }
  };
  const fetchPTAData = async () => {
    if (!ptaId) {
      setLoading(false);
      return;
    }
    
    setLoading(true);
    const {
      data: pta,
      error
    } = await supabase.from('ptas').select('*').eq('id', ptaId).single();
    if (error || !pta) {
      setPtaData(null);
    } else {
      setPtaData(pta);
      if (pta.id) {
        const {
          data: sponsorship
        } = await supabase.from('sponsorships').select('*').eq('pta_id', pta.id).eq('status', 'active').eq('show_on_fundraising', true).order('created_at', {
          ascending: false
        }).limit(1).maybeSingle();
        setSponsorshipData(sponsorship);

        // Fetch members with profiles
        const {
          data: membersData
        } = await supabase.from('members').select(`
            id,
            full_name,
            role,
            user_id,
            profiles!inner(avatar_url, hide_avatar_from_fundraising)
          `).eq('pta_id', pta.id).order('full_name');
        if (membersData) {
          setMembers(membersData);
        }
      }
    }
    setLoading(false);
  };
  const handleSignOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    toast({
      title: "Signed out",
      description: "You have been signed out successfully"
    });
  };
  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  if (loading) {
    return <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-muted-foreground">Loading...</p>
      </div>;
  }
  
  if (!ptaId && !user) {
    return <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="max-w-md mx-auto shadow-elevated">
          <CardHeader className="text-center">
            <CardTitle>Welcome to PTA Fundraising</CardTitle>
            <CardDescription>Please sign in to view your PTA's fundraising page</CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <Button onClick={() => navigate('/auth')}>
              Sign In
            </Button>
          </CardContent>
        </Card>
      </div>;
  }
  const headerColor = ptaData?.header_color || '#6366f1';
  const logoUrl = ptaData?.logo_url;
  const showMembers = ptaData?.show_pta_members !== false;
  const showContactDetails = ptaData?.show_contact_details !== false;
  
  const ptaMembers = [{
    name: "Sarah Smith",
    role: "Chair",
    bio: "Passionate about creating opportunities for our children",
    email: "sarah@example.com"
  }, {
    name: "John Doe",
    role: "Treasurer",
    bio: "Ensuring transparency in all our finances",
    email: "john@example.com"
  }, {
    name: "Emma Wilson",
    role: "Secretary",
    bio: "Keeping everyone organized and informed",
    email: "emma@example.com"
  }];
  return <div className="min-h-screen bg-background">
      {/* Modern Header with Navigation */}
      <header className="sticky top-0 z-50 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b border-border">
        <div className="container flex h-16 items-center justify-between">
          <div className="flex items-center gap-8">
            {/* Logo */}
            <Link to={`/fundraising/${ptaId}`} className="flex items-center gap-2">
              {logoUrl ? <img src={logoUrl} alt="PTA Logo" className="h-12 w-auto rounded object-cover" /> : <div className="w-10 h-10 rounded bg-primary/10 flex items-center justify-center">
                  <span className="text-lg">🏫</span>
                </div>}
              <span className="font-semibold text-lg hidden sm:inline-block">
                {ptaData?.name || 'PTA Fundraising'}
              </span>
            </Link>

            {/* Centered Navigation */}
            <nav className="hidden md:flex items-center gap-8 absolute left-1/2 transform -translate-x-1/2">
              <button onClick={() => scrollToSection('home')} className="text-sm font-medium hover:text-primary transition-colors">
                Home
              </button>
              <button onClick={() => scrollToSection('events')} className="text-sm font-medium hover:text-primary transition-colors">
                Events
              </button>
              <button onClick={() => scrollToSection('goals')} className="text-sm font-medium hover:text-primary transition-colors">
                Goals
              </button>
              <button onClick={() => scrollToSection('marketplace')} className="text-sm font-medium hover:text-primary transition-colors">
                Marketplace
              </button>
              <button onClick={() => scrollToSection('about')} className="text-sm font-medium hover:text-primary transition-colors">
                About
              </button>
            </nav>
          </div>

          {/* User Actions */}
          <div className="flex items-center gap-3">
            {user ? <>
                {/* Messages Icon */}
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="relative"
                  onClick={() => navigate(`/marketplace?pta=${ptaId}`)}
                >
                  <MessageCircle className="h-5 w-5" />
                  {unreadMessagesCount > 0 && (
                    <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-destructive text-destructive-foreground text-xs flex items-center justify-center font-semibold">
                      {unreadMessagesCount}
                    </span>
                  )}
                </Button>
                
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="relative h-9 w-9 rounded-full">
                      <Avatar className="h-9 w-9">
                        <AvatarImage src={user.user_metadata?.avatar_url} alt={user.email} />
                        <AvatarFallback className="bg-primary text-primary-foreground">
                          {user.email?.charAt(0).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <div className="flex items-center justify-start gap-2 p-2">
                      <div className="flex flex-col space-y-1 leading-none">
                        <p className="font-medium text-sm">{user.email}</p>
                      </div>
                    </div>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate(`/parent-portal/${ptaId}`)}>
                      <User className="mr-2 h-4 w-4" />
                      My Account
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate(`/marketplace?pta=${ptaId}`)}>
                      <ShoppingBag className="mr-2 h-4 w-4" />
                      Marketplace
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handleSignOut}>
                      <LogOut className="mr-2 h-4 w-4" />
                      Sign Out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </> : <>
                <Button size="sm" className="gap-2" onClick={() => setDonateOpen(true)}>
                  <Heart className="h-4 w-4" />
                  Donate
                </Button>
                <Button variant="ghost" size="sm" onClick={() => navigate('/auth')}>
                  <LogIn className="mr-2 h-4 w-4" />
                  Sign In
                </Button>
              </>}

            <Dialog open={donateOpen} onOpenChange={setDonateOpen}>
              <DialogTrigger asChild>
                <Button size="sm" className="gap-2 hidden">
                  <Heart className="h-4 w-4" />
                  Donate
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle>Make a Donation</DialogTitle>
                  <DialogDescription>
                    Your donation helps support our school community
                  </DialogDescription>
                </DialogHeader>
                <form className="space-y-4">
                  <div className="grid grid-cols-4 gap-2">
                    {['£5', '£10', '£25', '£50'].map(amount => <Button key={amount} type="button" variant={selectedAmount === amount ? "default" : "outline"} className="h-12" onClick={() => setSelectedAmount(amount)}>
                        {amount}
                      </Button>)}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="custom-amount">Custom Amount (£)</Label>
                    <Input id="custom-amount" type="number" placeholder="10.00" value={selectedAmount.replace('£', '')} onChange={e => setSelectedAmount(`£${e.target.value}`)} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="donor-name">Your Name</Label>
                    <Input id="donor-name" placeholder="John Smith" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="donor-email">Email Address</Label>
                    <Input id="donor-email" type="email" placeholder="you@example.com" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="donor-message">Message (Optional)</Label>
                    <Textarea id="donor-message" placeholder="Add a message of support..." rows={3} />
                  </div>
                  <Button type="submit" className="w-full" size="lg">
                    Complete Donation
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section id="home" className="relative overflow-hidden bg-gradient-hero py-20 md:py-32">
        {sponsorshipData && (
          <div className="absolute top-4 right-4 bg-background/95 backdrop-blur px-4 py-2 rounded-lg shadow-card">
            <p className="text-sm">
              <span className="text-muted-foreground">Sponsored by:</span>{' '}
              <span className="font-semibold text-primary">{sponsorshipData.business_name}</span>
            </p>
          </div>
        )}
        
        <div className="container">
          <div className="max-w-3xl mx-auto text-center space-y-6">
            <Badge variant="secondary" className="text-sm mb-2">
              Fundraising Page
            </Badge>
            <h1 className="text-4xl md:text-6xl font-bold tracking-tight">
              {ptaData?.hero_welcome_text || `Welcome to ${ptaData?.school_name || 'Our School'}`}
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              {ptaData?.hero_description_text || 'Join our community in creating amazing opportunities for our children through fundraising, events, and parent involvement.'}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
              <Button size="lg" className="gap-2" onClick={() => setDonateOpen(true)}>
                <Heart className="h-5 w-5" />
                Donate Now
              </Button>
              <Button size="lg" variant="outline" className="gap-2" onClick={() => scrollToSection('events')}>
                View Events
                <ArrowRight className="h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>

        {/* Stats Section */}
        <div className="container mt-16">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <Card className="text-center shadow-card">
              <CardContent className="pt-6">
                <div className="text-3xl font-bold text-primary mb-2">£8,250</div>
                <p className="text-sm text-muted-foreground">Raised This Year</p>
              </CardContent>
            </Card>
            <Card className="text-center shadow-card">
              <CardContent className="pt-6">
                <div className="text-3xl font-bold text-primary mb-2">12</div>
                <p className="text-sm text-muted-foreground">Active Projects</p>
              </CardContent>
            </Card>
            <Card className="text-center shadow-card">
              <CardContent className="pt-6">
                <div className="text-3xl font-bold text-primary mb-2">250+</div>
                <p className="text-sm text-muted-foreground">Community Members</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Events Section */}
      <section id="events" className="py-20 bg-background">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Upcoming Events</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Join us at our upcoming events and be part of our vibrant school community
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {events.length > 0 ? events.map((event, i) => <Card key={i} className="shadow-card hover:shadow-elevated transition-shadow">
                <CardHeader>
                  <div className="flex items-start justify-between mb-2">
                    <Calendar className="h-8 w-8 text-primary" />
                    <Badge variant="secondary">{new Date(event.event_date).toLocaleDateString()}</Badge>
                  </div>
                  <CardTitle className="text-xl">{event.name}</CardTitle>
                  <CardDescription>{event.description || 'Join us for this exciting event!'}</CardDescription>
                </CardHeader>
                <CardContent>
                  <Button className="w-full">Get Tickets</Button>
                </CardContent>
              </Card>) : <div className="col-span-full text-center py-12">
                <p className="text-muted-foreground">No upcoming events at the moment. Check back soon!</p>
              </div>}
          </div>
        </div>
      </section>

      {/* Goals Section */}
      <section id="goals" className="py-20 bg-muted/30">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Fundraising Goals</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              See the impact we're making together and support the causes that matter
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {goals.length > 0 ? goals.map((goal, i) => {
              const progress = goal.target_amount ? Math.round((goal.current_amount / goal.target_amount) * 100) : 0;
              return <Card key={i} className="shadow-card">
                <CardHeader>
                  <div className="flex items-center gap-2 mb-2">
                    <Target className="h-6 w-6 text-primary" />
                    <CardTitle className="text-xl">{goal.title}</CardTitle>
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-2xl font-bold text-primary">£{goal.current_amount?.toFixed(0) || 0}</span>
                    <span className="text-sm text-muted-foreground">of £{goal.target_amount?.toFixed(0) || 0}</span>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {goal.details && <p className="text-sm text-muted-foreground line-clamp-2">{goal.details}</p>}
                  <div className="space-y-2">
                    <div className="h-3 bg-secondary rounded-full overflow-hidden">
                      <div className="h-full bg-primary transition-all duration-500" style={{
                        width: `${Math.min(progress, 100)}%`
                      }} />
                    </div>
                    <p className="text-xs text-muted-foreground text-right">{progress}% funded</p>
                  </div>
                  <Button className="w-full" onClick={() => setDonateOpen(true)}>
                    Support This Goal
                  </Button>
                </CardContent>
              </Card>;
            }) : <div className="col-span-full text-center py-12">
                <p className="text-muted-foreground">No active fundraising goals at the moment. Check back soon!</p>
              </div>}
          </div>
        </div>
      </section>

      {/* Marketplace Section */}
      {ptaData?.show_marketplace_section && <section id="marketplace" className="py-20 bg-background">
          <div className="container">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Community Marketplace</h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                Find great deals on school essentials from our community
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
              {marketplaceItems.length > 0 ? (
                marketplaceItems.map((item) => (
                  <Card key={item.id} className="shadow-card hover:shadow-elevated transition-shadow">
                    <CardHeader>
                      <div className="w-full h-48 bg-muted/50 rounded-lg overflow-hidden mb-4">
                        {item.images && item.images.length > 0 ? (
                          <img 
                            src={item.images[0]} 
                            alt={item.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-6xl">
                            📦
                          </div>
                        )}
                      </div>
                      <CardTitle className="text-lg line-clamp-2">{item.name}</CardTitle>
                      {item.price && (
                        <div className="text-2xl font-bold text-primary">
                          £{parseFloat(item.price).toFixed(2)}
                        </div>
                      )}
                      <Badge variant="outline" className="w-fit">{item.category}</Badge>
                    </CardHeader>
                    <CardContent>
                      <Button 
                        variant="outline" 
                        className="w-full gap-2"
                        onClick={() => setSelectedMarketplaceItem(item)}
                      >
                        <ShoppingBag className="h-4 w-4" />
                        View Details
                      </Button>
                    </CardContent>
                  </Card>
                ))
              ) : (
                <div className="col-span-full text-center py-12 text-muted-foreground">
                  No items currently available in the marketplace
                </div>
              )}
            </div>

            <div className="text-center mt-8">
              <Button variant="outline" size="lg" onClick={() => navigate(`/marketplace?pta=${ptaId}`)}>
                View All Listings
              </Button>
            </div>
          </div>
        </section>}

      {/* About Section */}
      <section id="about" className="py-20 bg-muted/30">
        <div className="container max-w-4xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">About Our PTA</h2>
            <p className="text-muted-foreground">
              Learn more about who we are and what we do
            </p>
          </div>

          <Card className="shadow-card mb-12">
            <CardContent className="pt-6 space-y-4">
              <div className="text-lg whitespace-pre-wrap">
                {ptaData?.about_text || `Our PTA is dedicated to supporting ${ptaData?.school_name || 'our school'} and creating amazing opportunities for our children. We organize events, raise funds, and build a strong community where every parent can get involved.`}
              </div>

              {showContactDetails && <div className="pt-4 mt-4 border-t border-border space-y-2">
                  {ptaData?.charity_number && <p><strong>Charity Number:</strong> {ptaData.charity_number}</p>}
                  <p><strong>School:</strong> {ptaData?.school_name || 'N/A'}</p>
                  {ptaData?.shared_email && <p><strong>Email:</strong> {ptaData.shared_email}</p>}
                </div>}
            </CardContent>
          </Card>

          {/* Meet the Team */}
          {showMembers && ptaData?.show_meet_team_section && members.length > 0 && <div className="space-y-6">
              <div className="text-center">
                <h3 className="text-2xl font-bold mb-2 flex items-center justify-center gap-2">
                  <Users className="h-6 w-6 text-primary" />
                  Meet the Team
                </h3>
                <p className="text-muted-foreground">
                  The dedicated volunteers making it all happen
                </p>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {members.map(member => <Card key={member.id} className="shadow-card text-center">
                    <CardContent className="pt-6">
                      <div className="w-20 h-20 mx-auto rounded-full bg-gradient-primary flex items-center justify-center text-primary-foreground text-2xl font-bold mb-4 overflow-hidden">
                        {member.profiles?.avatar_url && !member.profiles?.hide_avatar_from_fundraising ? <img src={member.profiles.avatar_url} alt={member.full_name} className="w-full h-full object-cover" /> : <span>{member.full_name.split(' ').map((n: string) => n[0]).join('')}</span>}
                      </div>
                      <h4 className="font-semibold text-lg mb-1">{member.full_name}</h4>
                      <Badge variant="secondary" className="mb-3">{member.role}</Badge>
                    </CardContent>
                  </Card>)}
              </div>
            </div>}

          {/* Contact Form */}
          {showContactDetails && ptaData?.shared_email && <Card className="shadow-card mt-12">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Mail className="h-5 w-5 text-primary" />
                  Get in Touch
                </CardTitle>
                <CardDescription>
                  Have questions? Contact us at <a href={`mailto:${ptaData.shared_email}`} className="text-primary hover:underline">{ptaData.shared_email}</a>
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="contact-name">Name</Label>
                      <Input id="contact-name" placeholder="Your name" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="contact-email">Email</Label>
                      <Input id="contact-email" type="email" placeholder="your@email.com" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="contact-message">Message</Label>
                    <Textarea id="contact-message" placeholder="How can we help?" rows={4} />
                  </div>
                  <Button type="submit" className="w-full">Send Message</Button>
                </form>
              </CardContent>
            </Card>}
        </div>
        
        {/* Join CTA */}
        <div className="container max-w-4xl mx-auto mt-12">
          <Card className="shadow-elevated bg-gradient-hero border-0">
            <CardContent className="pt-6 text-center">
              <h3 className="text-2xl font-bold mb-2">Want to Get Involved?</h3>
              <p className="text-muted-foreground mb-6">
                Join our PTA and help make a difference in our school community
              </p>
              <Link to={`/become-a-member/${ptaId}`}>
                <Button size="lg" className="gap-2">
                  <Users className="h-5 w-5" />
                  Learn How to Join
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-card border-t border-border py-12">
        <div className="container">
          <div className="grid md:grid-cols-4 gap-8">
            {/* Brand */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                {logoUrl ? <img src={logoUrl} alt="PTA Logo" className="w-8 h-8 rounded object-cover" /> : <div className="w-8 h-8 rounded bg-primary/10 flex items-center justify-center">
                    <span className="text-lg">🏫</span>
                  </div>}
                <span className="font-semibold">{ptaData?.name || 'PTA'}</span>
              </div>
              <p className="text-sm text-muted-foreground">
                Supporting {ptaData?.school_name || 'our school'} community
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="font-semibold mb-3">Quick Links</h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <button onClick={() => scrollToSection('events')} className="text-muted-foreground hover:text-primary transition-colors">
                    Events
                  </button>
                </li>
                <li>
                  <button onClick={() => scrollToSection('goals')} className="text-muted-foreground hover:text-primary transition-colors">
                    Goals
                  </button>
                </li>
                <li>
              {ptaData?.show_marketplace_section && <button onClick={() => scrollToSection('marketplace')} className="text-muted-foreground hover:text-primary transition-colors">
                  Marketplace
                </button>}
                </li>
                <li>
                  <button onClick={() => scrollToSection('about')} className="text-muted-foreground hover:text-primary transition-colors">
                    About
                  </button>
                </li>
              </ul>
            </div>

            {/* Get Involved */}
            <div>
              <h4 className="font-semibold mb-3">Get Involved</h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <button onClick={() => setDonateOpen(true)} className="text-muted-foreground hover:text-primary transition-colors">
                    Donate
                  </button>
                </li>
                <li>
                  <Link to={`/become-a-member/${ptaId}`} className="text-muted-foreground hover:text-primary transition-colors">
                    Become a Member
                  </Link>
                </li>
              </ul>
            </div>

            {/* Policies */}
            <div>
              <h4 className="font-semibold mb-3">Policies</h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link to="/privacy" className="text-muted-foreground hover:text-primary transition-colors">
                    Privacy Policy
                  </Link>
                </li>
                <li>
                  <Link to="/terms" className="text-muted-foreground hover:text-primary transition-colors">
                    Terms of Service
                  </Link>
                </li>
                <li>
                  <Link to="/cookies" className="text-muted-foreground hover:text-primary transition-colors">
                    Cookie Policy
                  </Link>
                </li>
                <li>
                  <Link to="/refund" className="text-muted-foreground hover:text-primary transition-colors">
                    Refund Policy
                  </Link>
                </li>
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h4 className="font-semibold mb-3">Contact</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                {ptaData?.shared_email && <li>{ptaData.shared_email}</li>}
                {ptaData?.charity_number && <li>Charity: {ptaData.charity_number}</li>}
              </ul>
            </div>
          </div>

          <div className="border-t border-border mt-8 pt-8 text-center text-sm text-muted-foreground">
            <p>© {new Date().getFullYear()} {ptaData?.name || 'PTA'}. All rights reserved.</p>
          </div>
        </div>
      </footer>
      
      {/* Marketplace Item Details Modal */}
      {selectedMarketplaceItem && (
        <Dialog open={!!selectedMarketplaceItem} onOpenChange={(open) => !open && setSelectedMarketplaceItem(null)}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{selectedMarketplaceItem.name}</DialogTitle>
              <DialogDescription>
                <Badge variant="outline" className="mt-2">{selectedMarketplaceItem.category}</Badge>
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              {selectedMarketplaceItem.images && selectedMarketplaceItem.images.length > 0 && (
                <div className="grid grid-cols-3 gap-2">
                  {selectedMarketplaceItem.images.map((img: string, idx: number) => (
                    <img 
                      key={idx}
                      src={img} 
                      alt={`${selectedMarketplaceItem.name} ${idx + 1}`}
                      className="w-full aspect-square object-cover rounded-lg"
                    />
                  ))}
                </div>
              )}
              
              {selectedMarketplaceItem.price && (
                <div>
                  <h4 className="font-semibold mb-2">Price</h4>
                  <p className="text-2xl font-bold text-primary">
                    £{parseFloat(selectedMarketplaceItem.price).toFixed(2)}
                  </p>
                </div>
              )}

              <div className="flex gap-2">
                <Button 
                  className="flex-1"
                  onClick={() => {
                    setSelectedMarketplaceItem(null);
                    navigate(`/marketplace?pta=${ptaId}`);
                  }}
                >
                  View in Marketplace
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => setSelectedMarketplaceItem(null)}
                >
                  Close
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
      
      <CookieBanner />
    </div>;
}